import { useQuery } from '@tanstack/react-query';
import { merchantQueries } from '../api/merchant-queries';
import type {
  MerchantListParams,
  IntegrationTokenListParams,
} from '../lib/merchant-mappers';

/** Paginated, filtered merchants list. Keeps the previous page while loading. */
export function useMerchants(params: MerchantListParams) {
  return useQuery(merchantQueries.list(params));
}

/** Single merchant detail by id. */
export function useMerchant(id: string | undefined, enabled = true) {
  return useQuery(merchantQueries.detail(id ?? '', enabled && Boolean(id)));
}

/** Eats integration settings for a merchant. */
export function useMerchantIntegration(merchantId: string | undefined, enabled = true) {
  return useQuery(
    merchantQueries.integration(merchantId ?? '', enabled && Boolean(merchantId)),
  );
}

/** Select options for the Eats tab (menu sources + delivery providers). */
export function useMerchantIntegrationOptions(enabled = true) {
  return useQuery(merchantQueries.integrationOptions(enabled));
}

export function useMerchantIntegrationCredentials(
  merchantId: string | undefined,
  provider: string | undefined,
  enabled = true,
) {
  return useQuery(
    merchantQueries.integrationCredentials(merchantId ?? '', provider ?? '', enabled),
  );
}

export function useMerchantDeliveryPricing(
  merchantId: string | undefined,
  enabled = true,
) {
  return useQuery(
    merchantQueries.deliveryPricing(merchantId ?? '', enabled && Boolean(merchantId)),
  );
}

/** Pharma integration; a 404 means "never configured". */
export function usePharmaIntegration(companyId: string | undefined, enabled = true) {
  return useQuery(
    merchantQueries.pharmaIntegration(companyId ?? '', enabled && Boolean(companyId)),
  );
}

/** Brand staff (paginated; defaults to the first 20). */
export function useEmployees(
  merchantId: string | undefined,
  enabled = true,
  params?: { limit?: number; offset?: number },
) {
  return useQuery(
    merchantQueries.employees(merchantId ?? '', enabled && Boolean(merchantId), params),
  );
}

/** Single employee by id. */
export function useEmployee(employeeId: string | undefined) {
  return useQuery(merchantQueries.employee(employeeId ?? ''));
}

/** Vendor integration-token issuance list. */
export function useIntegrationTokens(params: IntegrationTokenListParams) {
  return useQuery(merchantQueries.integrationTokens(params));
}

/** Delivery-vendor mirror row for the delivery verification tab. */
export function useDeliveryVendor(merchantId: string | undefined, enabled = true) {
  return useQuery(
    merchantQueries.deliveryVendor(merchantId ?? '', enabled && Boolean(merchantId)),
  );
}
